import { create } from 'zustand';

/*
    Per-mailbox profile pictures, stored locally as data URLs.

    Deliberately client-side: JMAP has no place to put one, and the alternative
    would be inventing server state for a cosmetic preference. Images are
    downscaled to 128px and re-encoded before storing — a phone photo is several
    megabytes and localStorage gives us about five in total, so storing the
    original would blow the quota on the first upload.
*/

const KEY = 'webmail.avatars.v1';
const SIZE = 128;

function load(): Record<string, string> {
    try {
        return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, string>;
    } catch {
        return {};
    }
}

interface AvatarState {
    /** Keyed by mailbox address, so a picture survives signing out and back in. */
    byEmail: Record<string, string>;
    set: (email: string, dataUrl: string) => void;
    clear: (email: string) => void;
}

export const useAvatars = create<AvatarState>((set, get) => ({
    byEmail: load(),
    set: (email, dataUrl) => {
        const byEmail = { ...get().byEmail, [email.toLowerCase()]: dataUrl };
        try {
            localStorage.setItem(KEY, JSON.stringify(byEmail));
        } catch {
            /* quota — keep it for this session at least */
        }
        set({ byEmail });
    },
    clear: (email) => {
        const byEmail = { ...get().byEmail };
        delete byEmail[email.toLowerCase()];
        localStorage.setItem(KEY, JSON.stringify(byEmail));
        set({ byEmail });
    },
}));

export const avatarFor = (byEmail: Record<string, string>, email: string) =>
    byEmail[email.toLowerCase()];

/** Square-crops from the centre, scales to 128px and re-encodes. */
export function processImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement('canvas');
            canvas.width = SIZE;
            canvas.height = SIZE;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('canvas unavailable'));
                return;
            }
            const side = Math.min(img.width, img.height);
            ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, SIZE, SIZE);
            // PNG keeps transparency for logo-style images; JPEG would black it out.
            const png = canvas.toDataURL('image/png');
            const jpeg = canvas.toDataURL('image/jpeg', 0.85);
            resolve(png.length <= jpeg.length ? png : jpeg);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('that file is not an image the browser can read'));
        };
        img.src = url;
    });
}
